/** What the app rail carried, now inside every page's nav pane (one left
 *  column, 2026-10-02): the apps along its top (Mail, Calendar, Tasks,
 *  Assistant) and Settings, Appearance, Reload and the account along its
 *  bottom. As a row in the open pane, as a column in its folded strip.
 *  No logo: the window and the Dock already say what app this is. */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  ListTodo,
  LogOut,
  Mail as MailIcon,
  Palette,
  Pin,
  PinOff,
  Plane,
  RotateCw,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";
import { ThemePickerDropdown } from "./ThemePicker";
import { useUpdateAvailable } from "./AppUpdate";
import { useAuth } from "../lib/auth";

type Orientation = "row" | "column";

interface AppEntry {
  to: string;
  label: string;
  Icon: LucideIcon;
  matches: (pathname: string) => boolean;
  /** Where Settings opens from this app. */
  settings: string;
  active: string;
  idle: string;
}

const under = (base: string) => (p: string) => p === base || p.startsWith(`${base}/`);

export const APPS: AppEntry[] = [
  {
    to: "/mail",
    label: "Mail",
    Icon: MailIcon,
    matches: (p) => p.startsWith("/mail") || p === "/search",
    settings: "/settings/mail",
    active: "bg-sky-500 text-white shadow-sm",
    idle: "text-sky-500 dark:text-sky-400",
  },
  {
    to: "/calendar",
    label: "Calendar",
    Icon: CalendarIcon,
    matches: under("/calendar"),
    settings: "/settings/calendar",
    active: "bg-violet-500 text-white shadow-sm",
    idle: "text-violet-500 dark:text-violet-400",
  },
  {
    to: "/tasks",
    label: "Tasks",
    Icon: ListTodo,
    matches: under("/tasks"),
    settings: "/settings/mail",
    active: "bg-emerald-500 text-white shadow-sm",
    idle: "text-emerald-500 dark:text-emerald-400",
  },
  {
    to: "/travel",
    label: "Travel",
    Icon: Plane,
    matches: under("/travel"),
    settings: "/settings/calendar",
    active: "bg-amber-500 text-white shadow-sm",
    idle: "text-amber-500 dark:text-amber-400",
  },

];

/** The app the path belongs to (Settings belongs to none). */
export function appFor(pathname: string): AppEntry | undefined {
  return APPS.find((a) => a.matches(pathname));
}

/** The switcher: a row of tiles across the top of the open pane, a column
 *  at the top of the folded strip. */
export function AppSwitcher({ orientation }: { orientation: Orientation }) {
  const { pathname } = useLocation();
  if (orientation === "column") {
    return (
      <div className="flex flex-col items-center gap-1" role="navigation" aria-label="Apps">
        {APPS.map((a) => {
          const on = a.matches(pathname);
          return (
            <Link
              key={a.to}
              to={a.to}
              aria-current={on ? "page" : undefined}
              title={a.label}
              aria-label={a.label}
              className={"flex h-9 w-9 items-center justify-center rounded-lg transition-colors " + (on ? a.active : a.idle + " hover:bg-slate-200/60 dark:hover:bg-slate-700/40")}
            >
              <a.Icon className="h-[18px] w-[18px]" strokeWidth={on ? 2.5 : 2} />
            </Link>
          );
        })}
      </div>
    );
  }
  return (
    <div className="flex gap-1 px-2 pt-2" role="navigation" aria-label="Apps">
      {APPS.map((a) => {
        const on = a.matches(pathname);
        return (
          <Link
            key={a.to}
            to={a.to}
            aria-current={on ? "page" : undefined}
            title={a.label}
            className={
              "flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 transition-colors " +
              (on ? a.active : "text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/40")
            }
          >
            <a.Icon className={"h-[18px] w-[18px] " + (on ? "" : a.idle)} strokeWidth={on ? 2.5 : 2} />
            <span className="w-full truncate text-center text-[10px] font-semibold leading-tight">{a.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

const FOOT_BTN = "relative inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 transition-all duration-150 hover:bg-slate-200/60 active:scale-90 dark:hover:bg-slate-700/40";

/** Settings, Appearance, Reload, the account and the pin: a row along the
 *  bottom of the open pane, a column at the bottom of the strip. The pin is
 *  always last, so it is in the same place whether the pane is open or
 *  folded (2026-10-02). Icons carry a calm colour each. */
export function AppFooter({ orientation, pin }: { orientation: Orientation; pin?: { pinned: boolean; onToggle: () => void; what: string } }) {
  const { pathname } = useLocation();
  const settingsTo = appFor(pathname)?.settings ?? "/settings/mail";
  // In the strip every icon is 18 px, as the apps above them are.
  const ic = orientation === "column" ? "h-[18px] w-[18px]" : "h-4 w-4";
  const inSettings = pathname.startsWith("/settings") || pathname === "/mail/jobs";
  return (
    <div
      className={
        orientation === "row"
          ? "flex items-center gap-1 border-t border-slate-200/70 dark:border-slate-700/40 px-2 py-1.5"
          : "flex flex-col items-center gap-1"
      }
    >
      <Link
        to={settingsTo}
        className={FOOT_BTN + (inSettings ? " bg-slate-200/80 dark:bg-slate-700/60 text-slate-900 dark:text-white" : "")}
        title="Settings"
        aria-label="Settings"
        aria-current={inSettings ? "page" : undefined}
      >
        <SettingsIcon className={ic + " text-indigo-500 dark:text-indigo-400"} />
      </Link>
      <AppearanceButton orientation={orientation} />
      <ReloadButton iconClass={ic} />
      <AccountButton orientation={orientation} />
      {orientation === "row" && <div className="flex-1" />}
      {pin && (
        <button
          type="button"
          onClick={pin.onToggle}
          className={FOOT_BTN + (pin.pinned ? " bg-sky-100/80 dark:bg-sky-900/40" : "")}
          title={pin.pinned ? `Unpin: fold the ${pin.what} to a strip that opens on hover ([)` : `Pin: keep the ${pin.what} open ([)`}
          aria-label={pin.pinned ? "Unpin" : "Pin"}
          aria-pressed={pin.pinned}
        >
          {pin.pinned ? <Pin className={ic + " text-sky-600 dark:text-sky-400"} /> : <PinOff className={ic + " text-amber-500 dark:text-amber-400"} />}
        </button>
      )}
    </div>
  );
}

function AppearanceButton({ orientation }: { orientation: Orientation }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={FOOT_BTN + (open ? " bg-slate-200/80 dark:bg-slate-700/60" : "")}
        title="Appearance"
        aria-label="Appearance"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-nav-popover-open={open || undefined}
      >
        <Palette className={(orientation === "column" ? "h-[18px] w-[18px]" : "h-4 w-4") + " text-fuchsia-500 dark:text-fuchsia-400"} />
      </button>
      <ThemePickerDropdown open={open} onClose={() => setOpen(false)} anchor={orientation === "row" ? "top" : "right"} ignoreRef={ref} />
    </>
  );
}

/** Reload WOV: picks up a newer deployment without quitting the app (the
 *  Mac app has no browser reload button). A dot says a newer one is out. */
function ReloadButton({ iconClass }: { iconClass: string }) {
  const updated = useUpdateAvailable();
  const label = updated ? "Reload: a newer WOV is ready" : "Reload WOV";
  return (
    <button type="button" onClick={() => window.location.reload()} className={FOOT_BTN} title={label} aria-label={label}>
      <RotateCw className={iconClass + " " + (updated ? "text-sky-600 dark:text-sky-400" : "text-teal-600 dark:text-teal-400")} />
      {updated && <span aria-hidden className="absolute right-1 top-1 h-2 w-2 rounded-full bg-sky-500 ring-2 ring-white dark:ring-slate-900" />}
    </button>
  );
}

/** The avatar, and its menu (name, address, Sign out). The menu is put in
 *  <body> at a fixed spot measured on opening, so the pane's edges cannot
 *  clip it and the pane folding cannot move it. */
function AccountButton({ orientation }: { orientation: Orientation }) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(
      orientation === "row"
        ? { right: Math.max(8, window.innerWidth - r.right), bottom: window.innerHeight - r.top + 8 }
        : { left: r.right + 8, bottom: Math.max(8, window.innerHeight - r.bottom) },
    );
  }, [open, orientation]);

  // As the Appearance popover: any press elsewhere (caught in the capture
  // phase), Esc, or focus going into a message's frame closes it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onBlur = () => setOpen(false);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, [open]);

  const user = auth.user;
  const display = user?.name || user?.email || "Account";
  const initial = display.trim().charAt(0).toUpperCase() || "?";
  const showImg = !!user?.picture && !imgFailed;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={FOOT_BTN}
        title={display}
        aria-label={`Account menu, signed in as ${display}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-nav-popover-open={open || undefined}
      >
        {/* 18 px: a round photo reads as big as the 16 px glyphs beside it. */}
        <span className="flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 text-[9px] font-bold text-white ring-1 ring-slate-300 dark:ring-slate-600 select-none">
          {showImg ? <img src={user!.picture} alt="" referrerPolicy="no-referrer" onError={() => setImgFailed(true)} className="h-full w-full object-cover" /> : initial}
        </span>
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: "fixed", ...pos }}
          className="z-[70] w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="min-w-0 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{user?.name || "Signed in"}</div>
            {user?.email && <div className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</div>}
          </div>
          <a href="/auth/logout" role="menuitem" className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10">
            <LogOut className="h-4 w-4" />
            Sign out
          </a>
        </div>,
        document.body,
      )}
    </>
  );
}
